/**
 * Which device is looking at the page.
 *
 * Only used for things that are genuinely platform-specific — an Android
 * package is not installable on an iPhone, and no amount of feature detection
 * will tell you that. Anything that *can* be feature-detected should be, since
 * user agent strings are guesses and always have been.
 */

/**
 * An iPhone or iPad.
 *
 * The second test is for iPadOS 13 and later, which reports itself as a Mac.
 * `maxTouchPoints` is what separates it from an actual desktop — no Mac has a
 * touchscreen, and every iPad does.
 */
export function isIOS(agent = navigator.userAgent): boolean {
  if (/iPad|iPhone|iPod/.test(agent)) return true
  return /Macintosh/.test(agent)
    && typeof navigator !== 'undefined'
    && navigator.maxTouchPoints > 1
}

/**
 * The marker the Android WebView appends to its user agent.
 *
 * Set in capacitor.config.ts. See `isAppShell` for why it exists.
 */
export const APP_USER_AGENT_MARKER = 'CCISCodeHubApp'

/**
 * Running inside the packaged app.
 *
 * Judged by the user agent alone, deliberately.
 *
 * The obvious answer is `Capacitor.isNativePlatform()`, and that is the one
 * that failed: the WebView loads a remote origin, where the native bridge is
 * not reliably injected. With no bridge it answers false *inside the app*, the
 * session is written to sessionStorage, and Android throws it away with the
 * process — a cold-start logout indistinguishable from the session expiring.
 *
 * The user agent needs no injection. It is set by the native config before the
 * page loads and cannot silently stop working the way the bridge did.
 *
 * Callers that also want the bridge should ask for it themselves; authStorage
 * does, so a build older than this marker keeps working.
 */
export function isAppShell(agent = navigator.userAgent): boolean {
  if (agent.includes(APP_USER_AGENT_MARKER)) return true

  // Android stamps `; wv)` into the user agent of every WebView, and has done
  // for a decade. It is set by the system, not by this project's config, so
  // unlike the marker above it is already true in builds shipped before that
  // marker existed — which is most of the installs in the wild.
  //
  // A WebView belonging to some other app (an in-app browser) matches too.
  // That is fine and arguably right: it is a single-tab context with no tabs
  // to isolate from, which is the only thing the per-tab session buys.
  return / wv\)/.test(agent) || agent.includes('; wv)')
}

/**
 * Certainly *this* app — not merely some WebView.
 *
 * `isAppShell` deliberately matches any Android WebView, because for choosing
 * a storage backend that is the right question: a WebView is a single-tab
 * context either way, and old installs predate the marker.
 *
 * It is the wrong question for anything that changes what the product *does*.
 * Facebook, Instagram and Messenger all render links in an Android WebView, so
 * `isAppShell` is true for a student who merely tapped a shared link — and
 * that student was being redirected off the landing page, and having exam
 * fullscreen switched off for them. Neither is defensible; they are in a
 * browser, whatever the user agent says.
 *
 * This asks the narrower question, and answers it only from things this
 * project sets: the marker in capacitor.config.ts, or Capacitor's own bridge.
 * Nothing else can produce either.
 */
export function isOurApp(agent = navigator.userAgent): boolean {
  if (agent.includes(APP_USER_AGENT_MARKER)) return true

  // Older installs predate the marker. The bridge is unreliable inside a
  // remote-origin WebView — which is why the marker exists — but when it does
  // answer, it answers truthfully, so it is worth asking second.
  try {
    const capacitor = (window as {
      Capacitor?: { isNativePlatform?: () => boolean }
    }).Capacitor
    return Boolean(capacitor?.isNativePlatform?.())
  } catch {
    return false
  }
}

/** Already launched from the home screen, rather than in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  // iOS never implemented the standard query and uses its own flag instead.
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone
  return Boolean(iosStandalone)
    || Boolean(window.matchMedia?.('(display-mode: standalone)').matches)
}
